/**
 * ImageUploader - Reusable image upload component
 * 
 * Supports drag-and-drop file upload OR pasting a URL,
 * with crop/reposition, preview, progress, and delete.
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  Slider,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Close as CloseIcon,
  AddPhotoAlternate as AddPhotoIcon,
  Link as LinkIcon,
  Check as CheckIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  Crop as CropIcon,
} from '@mui/icons-material';
import Cropper from 'react-easy-crop';
import api from '../config/api';

const MIN_CROP_ZOOM = 0.4;
const MAX_CROP_ZOOM = 3;

// =====================================================
// Canvas-based crop utility
// =====================================================

function createImage(url) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    // Only set crossOrigin for remote URLs; data: and blob: URLs don't need it
    // and setting it can cause issues in some browsers.
    if (!url.startsWith('data:') && !url.startsWith('blob:')) {
      image.setAttribute('crossOrigin', 'anonymous');
    }
    image.src = url;
  });
}

async function getCroppedBlob(imageSrc, pixelCrop) {
  const image = await createImage(imageSrc);

  // Round to integers to avoid sub-pixel issues
  const sx = Math.round(pixelCrop.x);
  const sy = Math.round(pixelCrop.y);
  const sw = Math.round(pixelCrop.width);
  const sh = Math.round(pixelCrop.height);

  // Clamp to image bounds
  const iw = image.naturalWidth || image.width;
  const ih = image.naturalHeight || image.height;
  const clampedSx = Math.max(0, Math.min(sx, iw - 1));
  const clampedSy = Math.max(0, Math.min(sy, ih - 1));
  const clampedSw = Math.min(sw, iw - clampedSx);
  const clampedSh = Math.min(sh, ih - clampedSy);

  const canvas = document.createElement('canvas');
  canvas.width = clampedSw;
  canvas.height = clampedSh;
  const ctx = canvas.getContext('2d');

  ctx.drawImage(
    image,
    clampedSx, clampedSy, clampedSw, clampedSh,
    0, 0, clampedSw, clampedSh
  );

  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      resolve(blob);
    }, 'image/jpeg', 0.92);
  });
}

// =====================================================
// SingleImageUploader
// =====================================================

/**
 * Single Image Uploader (for cover images, avatars, logos)
 * 
 * Props:
 *  - value: current image URL
 *  - onChange: callback(url) when image is uploaded or removed
 *  - preset: 'cover' | 'gallery' | 'thumbnail' | 'itinerary'
 *  - folder: R2 storage folder
 *  - label: label text
 *  - height: preview height (default 200)
 *  - cropAspect: width/height ratio for crop (e.g. 16/9, 1 for square). null = free crop.
 *  - cropShape: 'rect' | 'round' (default 'rect')
 */
export function SingleImageUploader({ 
  value, 
  onChange, 
  preset = 'cover', 
  folder = 'packages/covers',
  label = 'Cover Image',
  height = 200,
  cropAspect,
  cropShape = 'rect',
  usePartnerAuth = false,
}) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [previewFailed, setPreviewFailed] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    setPreviewFailed(false);
  }, [value]);

  // Crop state — use a ref for croppedAreaPixels so the confirm handler
  // always reads the very latest coordinates (avoids React batching / stale closure issues).
  const [cropSrc, setCropSrc] = useState(null);   // data URL or object URL for cropper
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const croppedPixelsRef = useRef(null);

  const onCropComplete = useCallback((_, croppedPixels) => {
    croppedPixelsRef.current = croppedPixels;
  }, []);

  // Read file into object URL and open crop dialog
  const openCropper = useCallback((file) => {
    // Use object URL — faster and more memory-efficient than data URL
    const objectUrl = URL.createObjectURL(file);
    setCropSrc(objectUrl);
    setCrop({ x: 0, y: 0 });
    // Free-aspect logos start slightly zoomed out so a full lockup can fit.
    setZoom(cropAspect === null ? 0.65 : 1);
    croppedPixelsRef.current = null;
  }, [cropAspect]);

  // Upload the cropped blob
  const handleCropConfirm = useCallback(async () => {
    if (!cropSrc) return;

    // Prefer cropper output; if onCropComplete has not fired yet, use the full image.
    let pixelCrop = croppedPixelsRef.current;
    if (!pixelCrop) {
      try {
        const image = await createImage(cropSrc);
        const iw = image.naturalWidth || image.width;
        const ih = image.naturalHeight || image.height;
        pixelCrop = { x: 0, y: 0, width: iw, height: ih };
      } catch (err) {
        setError('Could not read image. Please try another file.');
        return;
      }
    }

    // Capture values before clearing state
    const src = cropSrc;

    setError(null);
    setUploading(true);
    setCropSrc(null);

    try {
      const blob = await getCroppedBlob(src, pixelCrop);
      if (!blob) {
        setError('Could not process image. Please try again.');
        return;
      }
      const fd = new FormData();
      fd.append('image', blob, 'cropped.jpg');

      const response = await api.post(
        `/uploads/image?preset=${preset}&folder=${folder}`,
        fd,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 60000,
          usePartnerAuth,
        }
      );

      if (response.data.success) {
        onChange(response.data.url);
        setShowUrlInput(false);
        setUrlInput('');
      } else {
        setError(response.data.error || 'Upload failed. Please try again.');
      }
    } catch (err) {
      console.error('Upload failed:', err);
      setError(err.response?.data?.error || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
      // Revoke object URL to free memory
      if (src && src.startsWith('blob:')) URL.revokeObjectURL(src);
    }
  }, [cropSrc, onChange, preset, folder, usePartnerAuth]);

  const handleCropCancel = useCallback(() => {
    const src = cropSrc;
    setCropSrc(null);
    if (src && src.startsWith('blob:')) URL.revokeObjectURL(src);
  }, [cropSrc]);

  // File validation then open cropper
  const handleFileSelected = useCallback((file) => {
    if (!file) return;

    // Mobile cameras often return empty MIME; accept any image/* and let the server reject bad files
    const type = (file.type || '').toLowerCase();
    const looksLikeImage = !type || type.startsWith('image/');
    if (!looksLikeImage) {
      setError('Invalid file type. Use JPEG, PNG, WebP, GIF, or AVIF.');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError('File too large. Maximum size is 10MB.');
      return;
    }

    setError(null);
    openCropper(file);
  }, [openCropper]);

  const handleUrlSubmit = useCallback(() => {
    const url = urlInput.trim();
    if (!url) return;
    try {
      new URL(url);
      onChange(url);
      setUrlInput('');
      setShowUrlInput(false);
      setError(null);
    } catch {
      setError('Please enter a valid URL.');
    }
  }, [urlInput, onChange]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelected(file);
  }, [handleFileSelected]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOver(false);
  }, []);

  const handleFileInput = useCallback((e) => {
    const file = e.target.files[0];
    if (file) handleFileSelected(file);
    e.target.value = '';
  }, [handleFileSelected]);

  const handleRemove = useCallback(async () => {
    if (value) {
      try {
        await api.delete('/uploads/image', { data: { url: value }, usePartnerAuth });
      } catch (err) {
        console.warn('Could not delete image from storage:', err);
      }
    }
    onChange('');
  }, [value, onChange, usePartnerAuth]);

  // Determine default aspect if not specified
  const effectiveAspect = cropAspect === undefined
    ? (preset === 'cover' ? 16 / 9 : preset === 'thumbnail' ? 1 : 4 / 3)
    : cropAspect;

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'text.secondary' }}>
        {label}
      </Typography>

      {/* Crop Dialog */}
      <Dialog
        open={!!cropSrc}
        onClose={handleCropCancel}
        maxWidth="sm"
        fullWidth
        sx={{ zIndex: (theme) => theme.zIndex.modal + 4 }}
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
      >
        <Box sx={{ px: 3, pt: 2.5, pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <CropIcon sx={{ color: '#591716', fontSize: 20 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Position your image
          </Typography>
        </Box>
        <DialogContent sx={{ p: 0, position: 'relative', height: 350, bgcolor: '#111' }}>
          {cropSrc && (
            <Cropper
              image={cropSrc}
              crop={crop}
              zoom={zoom}
              minZoom={MIN_CROP_ZOOM}
              maxZoom={MAX_CROP_ZOOM}
              restrictPosition={false}
              {...(effectiveAspect ? { aspect: effectiveAspect } : {})}
              cropShape={cropShape}
              showGrid={false}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, mr: 3 }}>
            <ZoomOutIcon sx={{ color: '#999', fontSize: 20 }} />
            <Slider
              value={zoom}
              min={MIN_CROP_ZOOM}
              max={MAX_CROP_ZOOM}
              step={0.05}
              onChange={(_, z) => setZoom(z)}
              sx={{
                color: '#591716',
                '& .MuiSlider-thumb': { width: 16, height: 16 },
              }}
            />
            <ZoomInIcon sx={{ color: '#999', fontSize: 20 }} />
            <Button
              size="small"
              onClick={() => setZoom(MIN_CROP_ZOOM)}
              sx={{ textTransform: 'none', minWidth: 0, color: '#666' }}
            >
              Fit
            </Button>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={handleCropCancel} sx={{ textTransform: 'none' }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleCropConfirm}
              sx={{
                textTransform: 'none',
                bgcolor: '#591716',
                '&:hover': { bgcolor: '#3d100f' },
              }}
            >
              Confirm & Upload
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Preview (has a working image) */}
      {value && !uploading && !previewFailed ? (
        <Box
          sx={{
            position: 'relative',
            borderRadius: cropShape === 'round' ? '50%' : 2,
            overflow: 'hidden',
            border: '2px solid',
            borderColor: 'divider',
            cursor: 'pointer',
            // For round shapes use fixed dimensions; for rectangular, use aspect ratio
            ...(cropShape === 'round'
              ? { height, width: height, mx: 'auto' }
              : effectiveAspect
                ? { width: '100%', aspectRatio: String(effectiveAspect) }
                : { width: '100%', height, minHeight: height }
            ),
          }}
          onClick={() => fileInputRef.current?.click()}
        >
          <Box
            component="img"
            src={value}
            alt={label}
            onError={() => setPreviewFailed(true)}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: cropShape === 'round' ? 'cover' : 'contain',
              display: 'block',
              bgcolor: '#f5f5f5',
            }}
          />
          <IconButton
            onClick={(event) => {
              event.stopPropagation();
              handleRemove();
            }}
            sx={{
              position: 'absolute',
              top: 8,
              right: cropShape === 'round' ? 8 : 8,
              bgcolor: 'rgba(0,0,0,0.6)',
              color: 'white',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
              width: 32,
              height: 32,
            }}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      ) : cropShape === 'round' ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Box
            onClick={() => !uploading && !showUrlInput && fileInputRef.current?.click()}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            sx={{
              width: height,
              height,
              border: '2px dashed',
              borderColor: dragOver ? '#591716' : 'divider',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: uploading || showUrlInput ? 'default' : 'pointer',
              bgcolor: dragOver ? 'rgba(89, 23, 22, 0.04)' : 'grey.50',
              transition: 'all 0.2s ease',
              '&:hover': !showUrlInput ? {
                borderColor: '#591716',
                bgcolor: 'rgba(89, 23, 22, 0.04)',
              } : {},
            }}
          >
            {uploading
              ? <CircularProgress size={28} sx={{ color: '#591716' }} />
              : <UploadIcon sx={{ fontSize: 32, color: '#999' }} />}
          </Box>
          {!uploading && (
            <Box sx={{ mt: 1.25, textAlign: 'center', maxWidth: 260 }}>
              {showUrlInput ? (
                <Box sx={{ width: '100%' }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="https://example.com/image.jpg"
                    value={urlInput}
                    onChange={(e) => setUrlInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleUrlSubmit(); }}
                    autoFocus
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LinkIcon sx={{ fontSize: 18, color: '#999' }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={handleUrlSubmit} disabled={!urlInput.trim()}>
                            <CheckIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 1 }}
                  />
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                    <Button
                      size="small"
                      onClick={() => { setShowUrlInput(false); setUrlInput(''); setError(null); }}
                      sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => { setShowUrlInput(false); fileInputRef.current?.click(); }}
                      sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                    >
                      Upload file instead
                    </Button>
                  </Box>
                </Box>
              ) : (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Drag & drop or click to browse
                  </Typography>
                  <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mb: 0.25 }}>
                    JPEG, PNG, WebP up to 10MB
                  </Typography>
                  <Button
                    size="small"
                    variant="text"
                    startIcon={<LinkIcon sx={{ fontSize: 14 }} />}
                    onClick={(e) => { e.stopPropagation(); setShowUrlInput(true); }}
                    sx={{ textTransform: 'none', fontSize: '0.75rem', color: '#666' }}
                  >
                    or paste image URL
                  </Button>
                </>
              )}
            </Box>
          )}
        </Box>
      ) : (
        <Box
          onClick={() => !uploading && !showUrlInput && fileInputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          sx={{
            height: showUrlInput ? 'auto' : height,
            minHeight: showUrlInput ? 0 : height,
            border: '2px dashed',
            borderColor: dragOver ? '#591716' : 'divider',
            borderRadius: 2,
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: uploading || showUrlInput ? 'default' : 'pointer',
            bgcolor: dragOver ? 'rgba(89, 23, 22, 0.04)' : 'grey.50',
            transition: 'all 0.2s ease',
            p: showUrlInput ? 2 : 0,
            '&:hover': !showUrlInput ? {
              borderColor: '#591716',
              bgcolor: 'rgba(89, 23, 22, 0.04)',
            } : {},
          }}
        >
          {uploading ? (
            <>
              <CircularProgress size={36} sx={{ color: '#591716', mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                Uploading...
              </Typography>
            </>
          ) : showUrlInput ? (
            <Box sx={{ width: '100%' }}>
              <TextField
                fullWidth
                size="small"
                placeholder="https://example.com/image.jpg"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleUrlSubmit(); }}
                autoFocus
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LinkIcon sx={{ fontSize: 18, color: '#999' }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={handleUrlSubmit} disabled={!urlInput.trim()}>
                        <CheckIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 1 }}
              />
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Button
                  size="small"
                  onClick={() => { setShowUrlInput(false); setUrlInput(''); setError(null); }}
                  sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                >
                  Cancel
                </Button>
                <Button
                  size="small"
                  variant="text"
                  onClick={() => { setShowUrlInput(false); fileInputRef.current?.click(); }}
                  sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                >
                  Upload file instead
                </Button>
              </Box>
            </Box>
          ) : (
            <>
              <UploadIcon sx={{ fontSize: 36, color: '#999', mb: 0.5 }} />
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                Drag & drop or click to browse
              </Typography>
              <Typography variant="caption" color="text.disabled" sx={{ mb: 0.5 }}>
                JPEG, PNG, WebP up to 10MB
              </Typography>
              <Button
                size="small"
                variant="text"
                startIcon={<LinkIcon sx={{ fontSize: 14 }} />}
                onClick={(e) => { e.stopPropagation(); setShowUrlInput(true); }}
                sx={{ textTransform: 'none', fontSize: '0.75rem', color: '#666' }}
              >
                or paste image URL
              </Button>
            </>
          )}
        </Box>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileInput}
        style={{ display: 'none' }}
      />
      {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
    </Box>
  );
}

// =====================================================
// MultiImageUploader (unchanged - no crop needed for galleries)
// =====================================================

/**
 * Multi Image Uploader (for gallery images)
 */
export function MultiImageUploader({
  value = [],
  onChange,
  preset = 'gallery',
  folder = 'packages/gallery',
  label = 'Gallery Images',
  maxImages = 20,
}) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [showUrlInput, setShowUrlInput] = useState(false);
  const fileInputRef = useRef(null);

  const handleUpload = useCallback(async (files) => {
    if (!files || files.length === 0) return;

    const remaining = maxImages - value.length;
    if (remaining <= 0) {
      setError(`Maximum ${maxImages} images allowed.`);
      return;
    }

    const filesToUpload = Array.from(files).slice(0, remaining);
    
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/avif'];
    for (const file of filesToUpload) {
      if (!allowedTypes.includes(file.type)) {
        setError(`Invalid file type: ${file.name}. Use JPEG, PNG, WebP, GIF, or AVIF.`);
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        setError(`File too large: ${file.name}. Maximum size is 10MB.`);
        return;
      }
    }

    setError(null);
    setUploading(true);

    try {
      const formData = new FormData();
      filesToUpload.forEach(file => formData.append('images', file));

      const response = await api.post(
        `/uploads/images?preset=${preset}&folder=${folder}`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 120000,
        }
      );

      if (response.data.success) {
        const newUrls = response.data.images.map(img => img.url);
        onChange([...value, ...newUrls]);
      }
    } catch (err) {
      console.error('Gallery upload failed:', err);
      setError(err.response?.data?.error || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  }, [value, onChange, preset, folder, maxImages]);

  const handleUrlSubmit = useCallback(() => {
    const url = urlInput.trim();
    if (!url) return;
    try {
      new URL(url);
      if (value.length >= maxImages) {
        setError(`Maximum ${maxImages} images allowed.`);
        return;
      }
      onChange([...value, url]);
      setUrlInput('');
      setShowUrlInput(false);
      setError(null);
    } catch {
      setError('Please enter a valid URL.');
    }
  }, [urlInput, value, onChange, maxImages]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    handleUpload(e.dataTransfer.files);
  }, [handleUpload]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOver(false);
  }, []);

  const handleFileSelect = useCallback((e) => {
    handleUpload(e.target.files);
    e.target.value = '';
  }, [handleUpload]);

  const handleRemove = useCallback(async (index) => {
    const url = value[index];
    if (url) {
      try {
        await api.delete('/uploads/image', { data: { url } });
      } catch (err) {
        console.warn('Could not delete image from storage:', err);
      }
    }
    const updated = value.filter((_, i) => i !== index);
    onChange(updated);
  }, [value, onChange]);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
          {label}
        </Typography>
        <Typography variant="caption" color="text.disabled">
          {value.length} / {maxImages}
        </Typography>
      </Box>

      {value.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
          {value.map((url, index) => (
            <Box
              key={index}
              sx={{
                position: 'relative',
                width: 120,
                height: 90,
                borderRadius: 1.5,
                overflow: 'hidden',
                border: '2px solid',
                borderColor: 'divider',
              }}
            >
              <Box
                component="img"
                src={url}
                alt={`Gallery ${index + 1}`}
                sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
              <IconButton
                onClick={() => handleRemove(index)}
                sx={{
                  position: 'absolute',
                  top: 2,
                  right: 2,
                  bgcolor: 'rgba(0,0,0,0.6)',
                  color: 'white',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
                  width: 24,
                  height: 24,
                  p: 0,
                }}
                size="small"
              >
                <CloseIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}

      {value.length < maxImages && (
        <>
          {showUrlInput ? (
            <Box sx={{ border: '2px dashed', borderColor: 'divider', borderRadius: 2, p: 2, bgcolor: 'grey.50' }}>
              <TextField
                fullWidth
                size="small"
                placeholder="https://example.com/image.jpg"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleUrlSubmit(); }}
                autoFocus
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LinkIcon sx={{ fontSize: 18, color: '#999' }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={handleUrlSubmit} disabled={!urlInput.trim()}>
                        <CheckIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 1 }}
              />
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Button
                  size="small"
                  onClick={() => { setShowUrlInput(false); setUrlInput(''); setError(null); }}
                  sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                >
                  Cancel
                </Button>
                <Button
                  size="small"
                  variant="text"
                  onClick={() => { setShowUrlInput(false); fileInputRef.current?.click(); }}
                  sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                >
                  Upload file instead
                </Button>
              </Box>
            </Box>
          ) : (
            <Box
              onClick={() => !uploading && fileInputRef.current?.click()}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              sx={{
                height: 100,
                border: '2px dashed',
                borderColor: dragOver ? '#591716' : 'divider',
                borderRadius: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: uploading ? 'default' : 'pointer',
                bgcolor: dragOver ? 'rgba(89, 23, 22, 0.04)' : 'grey.50',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: '#591716',
                  bgcolor: 'rgba(89, 23, 22, 0.04)',
                },
              }}
            >
              {uploading ? (
                <>
                  <CircularProgress size={28} sx={{ color: '#591716', mb: 0.5 }} />
                  <Typography variant="caption" color="text.secondary">
                    Uploading...
                  </Typography>
                </>
              ) : (
                <>
                  <AddPhotoIcon sx={{ fontSize: 24, color: '#999', mb: 0.5 }} />
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Add photos (drag & drop or click)
                  </Typography>
                  <Button
                    size="small"
                    variant="text"
                    startIcon={<LinkIcon sx={{ fontSize: 12 }} />}
                    onClick={(e) => { e.stopPropagation(); setShowUrlInput(true); }}
                    sx={{ textTransform: 'none', fontSize: '0.7rem', color: '#666', mt: 0.25, minHeight: 0, py: 0 }}
                  >
                    or paste URL
                  </Button>
                </>
              )}
            </Box>
          )}
        </>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />
      {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
    </Box>
  );
}

export default SingleImageUploader;
